import { useEffect, type FC, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import slugify from 'dibs-slugify/exports/slugify.ts';
import classnames from 'classnames';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import { ALWAYS_LINKABLE_LINK } from 'dibs-elements/exports/SeoLink.tsx';

import dibsCss from 'dibs-css';
import styles from './SiteNav-style.scss';

import { type SiteNavItem_navigationModule$key } from './__generated__/SiteNavItem_navigationModule.graphql.ts';

const { EVENT_NAVIGATION, EVENT_PROMO_INTERACTION } = eventNameConstants;

const { INTERACTION_TYPE_GLOBAL_NAV } = interactionTypeConstants;
const {
    STEP_INTERACTION_GLOBAL_NAV_DROPDOWN_DISPLAYED,
    STEP_INTERACTION_GLOBAL_NAV_DROPDOWN_CLICK,
} = stepInteractionConstants;

type Props = {
    navigationModule: SiteNavItem_navigationModule$key;
    isNavigationHovered: boolean;
    isVisible: boolean;
    onShow: () => void;
    onHide: () => void;
};

export const SiteNavItem: FC<Props> = ({
    navigationModule: navigationModuleRef,
    isNavigationHovered,
    isVisible,
    onShow,
}) => {
    const navigationModule = useFragment(
        graphql`
            fragment SiteNavItem_navigationModule on NavigationModule {
                name
                isHighlighted
                linkData {
                    ...SeoLink_linkData
                }
                items {
                    name
                }
            }
        `,
        navigationModuleRef
    );

    const { name, linkData, items, isHighlighted } = navigationModule || {};

    useEffect(() => {
        if (isVisible && items && items.length) {
            trackEvent(
                {
                    action: 'global nav dropdown displayed',
                    category: 'promo interaction',
                    label: name || '',
                    isInteractiveEvent: true,
                    eventName: EVENT_PROMO_INTERACTION,
                    interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                    step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_DROPDOWN_DISPLAYED,
                    trigger: name || '',
                },
                null
            );
        }
    }, [isVisible, items, name]);

    const handleClick = (event: MouseEvent): void => {
        const label = `${name?.toLowerCase() || ''} header link|${name?.toLowerCase() || ''}`;
        trackEvent(
            {
                action: 'header navigation click',
                category: 'navigation',
                label,
                eventName: EVENT_NAVIGATION,
                interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_DROPDOWN_CLICK,
                trigger: label,
            },
            event
        );
    };

    const isNavHoveredButNotActive = isNavigationHovered && !isVisible;
    const itemLinkClasses = classnames(styles.itemLink, {
        [styles.pale]: isNavHoveredButNotActive,
        [styles.isHighlightedPale]: isHighlighted && isNavHoveredButNotActive,
        [styles.isHighlighted]: isHighlighted && !isNavHoveredButNotActive,
    });

    return (
        <li className={dibsCss.flex} onMouseEnter={onShow}>
            <SeoLink
                dataTn={`global-nav-item-link-${slugify(name).toLowerCase()}`}
                className={itemLinkClasses}
                onClick={handleClick}
                variant={ALWAYS_LINKABLE_LINK}
                linkData={linkData}
            >
                {name}
                {isVisible && (
                    <div
                        className={classnames(
                            dibsCss.absolute,
                            dibsCss.h3px,
                            dibsCss.bottom0,
                            dibsCss.wFull,
                            dibsCss.left0,
                            dibsCss.bgNoir
                        )}
                    />
                )}
            </SeoLink>
        </li>
    );
};
